/**
 * "Why are you leaving?" on the delete-account screen (owner ask, 2026-10-01).
 *
 * Every deletion so far happened inside the first session and none said why.
 * The answer is optional and never changes the deletion; the Worker keeps it
 * on the deletion job (cloudflare/src/lib/leaveReason.ts).
 *
 * The KEYS mirror LEAVE_REASONS in cloudflare/src/lib/leaveReason.ts (the
 * Worker cannot import from the client); client/src/test/leaveReasons.test.ts
 * fails if the two lists drift. The labels live here only.
 */

export const LEAVE_REASONS = [
  "scanner",
  "trying",
  "setup",
  "missing",
  "fresh_start",
  "privacy",
  "other",
] as const;

export type LeaveReason = (typeof LEAVE_REASONS)[number];

/** What the trainer sees: their words, first person. */
export const LEAVE_REASON_LABELS: Record<LeaveReason, string> = {
  scanner: "Live Scan didn't work on my phone",
  trying: "I was just trying it out",
  setup: "Too much to set up",
  missing: "It doesn't do what I need",
  fresh_start: "I want to start over",
  privacy: "Privacy",
  other: "Something else",
};

/** The admin's short name for each, for tallies and table cells. */
export const LEAVE_REASON_SHORT: Record<LeaveReason, string> = {
  scanner: "Scanner failed",
  trying: "Just trying",
  setup: "Setup too much",
  missing: "Missing a feature",
  fresh_start: "Start over",
  privacy: "Privacy",
  other: "Other",
};

export const LEAVE_DETAIL_MAX = 500;

export function isLeaveReason(v: unknown): v is LeaveReason {
  return (LEAVE_REASONS as readonly unknown[]).includes(v);
}

/**
 * The POST /api/account/delete body: undefined when the trainer said nothing,
 * so a skipped question sends exactly what older builds always sent.
 */
export function leaveRequestBody(
  reason: LeaveReason | null,
  detail: string,
): { reason: LeaveReason | null; detail: string | null } | undefined {
  const d = detail.trim().slice(0, LEAVE_DETAIL_MAX);
  if (!reason && !d) return undefined;
  return { reason, detail: d || null };
}
